import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconTrash } from '@components/icons/trash';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { StatusOptionLabel } from '@components/status-option-label';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { endpointColorMap } from '../common';
import { useDeleteIntegrationWebhook } from '../integrations-hooks';
import { EndpointMethod, endpointMethods, type IntegrationWebhook } from '../integrations-types';

export const useDeleteWebhookModal = () => useModal(DeleteWebhookModal, { variant: 'LARGE' });

type DeleteWebhookFormData = {
  url: string;
  method: EndpointMethod;
};

const Form = createForm<DeleteWebhookFormData>();

const DeleteWebhookModal = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const deleteMutation = useDeleteIntegrationWebhook(webhook.shortId);
  const allowedMethods = endpointMethods.filter((method) => method !== 'GET');
  const urlValue = formStore.useFieldValue('url');

  const handleDelete = async (data?: DeleteWebhookFormData) => {
    await deleteMutation.mutateAsync(data);
    hideModal();
  };

  if (!webhook.isSubscribable || !webhook.responseValues) {
    return (
      <Modal>
        <Modal.SimpleHeader title={t('deleteWebhook')} icon={<IconFormIndicatorWarning />} />
        <Modal.Content>
          <Stack space={4}>
            <Text text={t('deleteWarning')} />
            <Text color="red100" text={t('cannotBeUndone')} />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
            <Button
              onClick={() => handleDelete()}
              disabled={deleteMutation.isLoading}
              variant="primary-red"
              iconLeft={<IconTrash />}
              text={t('core.delete')}
            />
          </Stack>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleDelete}>
        <Modal.SimpleHeader title={t('deleteWebhook')} icon={<IconFormIndicatorWarning />} />
        <Modal.Content>
          <Stack space={4}>
            <Text text={t('deleteWarning')} />
            <Text text={t('deleteSubscribableDescription')} />
            <Form.TextField
              id="url"
              label={t('unsubscribeUrl')}
              validators={['valid-http-url', 'required']}
            />
            <Form.Select
              id="method"
              label={t('method')}
              validators={['required']}
              options={allowedMethods.map((endpointMethod) => ({
                label: (
                  <StatusOptionLabel
                    text={endpointMethod}
                    color={endpointColorMap[endpointMethod]}
                  />
                ),
                value: endpointMethod,
              }))}
              isClearable={false}
              defaultValue="DELETE"
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              variant="primary-red"
              iconLeft={<IconTrash />}
              text={t('core.delete')}
              disabled={deleteMutation.isLoading || !urlValue}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    deleteWebhook: 'Delete webhook',
    deleteWarning:
      'You are about to permanently delete this webhook. The following will be affected:\n\u2022 All current configurations and response values\n\u2022 Journeys that rely on this endpoint\n\u2022 Travellers who are currently in progress\n\nOnce deleted, you can recreate this endpoint, but any lost data or configurations cannot be restored.',
    deleteSubscribableDescription:
      'This webhook is subscribed to an external service. Enter the unsubscribe URL and method to unsubscribe before deleting.',
    unsubscribeUrl: 'Unsubscribe URL',
    method: 'Method',
    cannotBeUndone: 'This can not be undone.',
  },
};
